// Realtime WebSocket client (browser transport singleton).
//
// One WebSocket per tab to the user's own cell's gateway (/v1/ws). Responsible
// for:
//   * deriving the wss:// URL from the runtime cell API base URL (so it follows
//     the same multi-cell routing as REST), with a REACT_APP_WS_URL override
//     for local dev where the gateway is a separate port;
//   * authenticating on connect via the ?access_token= query param (browsers
//     cannot set WS headers);
//   * a REF-COUNTED subscription manager: components/sagas call subscribe/
//     unsubscribe(channel); the socket only sends a server `unsubscribe` when
//     the last interested caller drops it. All active channels are re-sent
//     automatically after every (re)connect;
//   * exponential-backoff reconnect (capped), and an app-level ping to keep
//     intermediaries from idling the socket out.
//
// It is intentionally framework-agnostic (no redux import). The redux saga in
// sagas/realtime/ subscribes to its listeners and bridges events into actions.

import config from '../config';
import { getLocalUser } from '../utils/localUser';

const PROTO_PING_MS = 25_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 30_000;

// Resolve the WebSocket base URL. Prefer an explicit override (local dev,
// where the gateway is on its own port and not behind the API ingress);
// otherwise convert the runtime cell API base URL's scheme to ws(s) and append
// the gateway path. e.g. https://eu.api.predict.ai/v1 -> wss://eu.api.predict.ai/v1/ws
function resolveWsUrl() {
  const override =
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_WS_URL) ||
    (config && config.WS_URL) ||
    '';
  if (override) return override.replace(/\/+$/, '');
  const apiBase = (config.API_URL || '').replace(/\/+$/, '');
  if (!apiBase) return '';
  const wsBase = apiBase.replace(/^http/i, 'ws');
  return `${wsBase}/ws`;
}

class RealtimeClient {
  constructor() {
    this.ws = null;
    this.url = '';
    // channel -> refcount
    this.subscriptions = new Map();
    this.listeners = { event: new Set(), status: new Set(), welcome: new Set() };
    this.status = 'idle';
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.shouldRun = false;
    this._nextReqId = 1;
  }

  // -- listener registry ---------------------------------------------------

  on(type, cb) {
    if (!this.listeners[type]) this.listeners[type] = new Set();
    this.listeners[type].add(cb);
    return () => this.listeners[type].delete(cb);
  }

  _emit(type, payload) {
    const set = this.listeners[type];
    if (!set) return;
    set.forEach((cb) => {
      try { cb(payload); } catch (e) { /* listener errors are non-fatal */ }
    });
  }

  _setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this._emit('status', status);
  }

  // -- lifecycle -----------------------------------------------------------

  connect() {
    this.shouldRun = true;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return; // already connected/connecting (idempotent)
    }
    this._open();
  }

  _open() {
    const headers = getLocalUser();
    const auth = headers['Authorization'];
    const token = auth ? auth.replace(/^Bearer\s+/i, '') : '';
    const base = resolveWsUrl();
    if (!base || !token) {
      // No cell URL yet or not signed in — retry shortly; cellRouting /
      // login will populate these.
      this._setStatus('unavailable');
      if (this.shouldRun) this._scheduleReconnect();
      return;
    }
    this.url = `${base}?access_token=${encodeURIComponent(token)}`;
    this._setStatus('connecting');

    let ws;
    try {
      ws = new WebSocket(this.url);
    } catch (e) {
      this._setStatus('unavailable');
      this._scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._setStatus('open');
      this._resubscribeAll();
      this._startPing();
    };

    ws.onmessage = (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch (e) { return; }
      if (!msg || typeof msg !== 'object') return;
      if (msg.type === 'welcome') {
        this._emit('welcome', msg);
      } else if (msg.type === 'event') {
        this._emit('event', msg);
      }
      // ack / pong / error frames are currently informational; surface
      // errors to listeners for debugging without breaking the stream.
      else if (msg.type === 'error') {
        this._emit('event', msg);
      }
    };

    ws.onclose = (evt) => {
      this._stopPing();
      this.ws = null;
      // 4401 = unauthorized: token is bad/expired. Don't hammer-reconnect;
      // a token refresh / re-login will call connect() again.
      if (evt && evt.code === 4401) {
        this._setStatus('unauthorized');
        return;
      }
      this._setStatus('closed');
      if (this.shouldRun) this._scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose will follow and handle reconnect.
      this._setStatus('connecting');
    };
  }

  disconnect() {
    this.shouldRun = false;
    this._clearReconnect();
    this._stopPing();
    if (this.ws) {
      try { this.ws.close(1000, 'client_disconnect'); } catch (e) { /* noop */ }
    }
    this.ws = null;
    this.subscriptions.clear();
    this._setStatus('closed');
  }

  // Re-open with a fresh token (call after token refresh so a long-lived
  // socket doesn't outlive its JWT's api-key claim window).
  reconnectWithFreshToken() {
    if (!this.shouldRun) return;
    if (this.ws) {
      try { this.ws.close(1000, 'token_refresh'); } catch (e) { /* noop */ }
    }
    this.ws = null;
    this._open();
  }

  _scheduleReconnect() {
    this._clearReconnect();
    const delay = Math.min(
      BACKOFF_MAX_MS,
      BACKOFF_BASE_MS * 2 ** this.reconnectAttempts,
    );
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.shouldRun) this._open();
    }, delay);
  }

  _clearReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _startPing() {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      this._send({ action: 'ping', id: this._reqId() });
    }, PROTO_PING_MS);
  }

  _stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // -- subscription manager (ref-counted) ----------------------------------

  subscribe(channel) {
    if (!channel) return;
    const next = (this.subscriptions.get(channel) || 0) + 1;
    this.subscriptions.set(channel, next);
    if (next === 1) {
      this._send({ action: 'subscribe', channel, id: this._reqId() });
    }
  }

  unsubscribe(channel) {
    if (!channel) return;
    const cur = this.subscriptions.get(channel) || 0;
    if (cur <= 1) {
      this.subscriptions.delete(channel);
      this._send({ action: 'unsubscribe', channel, id: this._reqId() });
    } else {
      this.subscriptions.set(channel, cur - 1);
    }
  }

  _resubscribeAll() {
    this.subscriptions.forEach((_count, channel) => {
      this._send({ action: 'subscribe', channel, id: this._reqId() });
    });
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(obj)); } catch (e) { /* dropped; resubscribe on reconnect */ }
    }
    // When not open, subscribe/unsubscribe intent is preserved in the
    // subscriptions map and replayed by _resubscribeAll on the next open.
  }

  _reqId() {
    this._nextReqId += 1;
    return `c${this._nextReqId}`;
  }
}

const realtimeClient = new RealtimeClient();
export default realtimeClient;
